/**
 * PUBLIC Client Intake Landing (Iter121A)
 *
 * Reached from the "New Client Intake" button on the login screen. No auth
 * required. Explains the service to prospective clients and hosts a
 * detailed intake form. On submit the payload is POST'ed to
 *   /api/client-intake/submit
 * which lands it in the internal Client Intake Module for admin review.
 *
 * Kept intentionally lightweight (single file) so it stays fast to render
 * on any device the prospect happens to be using.
 */
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../config/canonical';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  ShieldCheck, Eye, Lock, Clock, Award, ChevronRight,
  Briefcase, Users, Car, Search, Scale, Camera, FileSearch,
  UserX, AlertCircle, CheckCircle2, Loader2, Plus, X, Gavel, FileWarning,
} from 'lucide-react';

const CASE_TYPES = [
  { value: 'DOMESTIC_INVESTIGATION', label: 'Domestic Investigation', icon: Users },
  { value: 'INFIDELITY', label: 'Infidelity / Cheating Spouse', icon: UserX },
  { value: 'CHILD_CUSTODY', label: 'Child Custody / Family Court', icon: Users },
  { value: 'SURVEILLANCE', label: 'Surveillance Request', icon: Camera },
  { value: 'BACKGROUND_CHECK', label: 'Background Investigation', icon: FileSearch },
  { value: 'ASSET_SEARCH', label: 'Asset Search / Hidden Assets', icon: Search },
  { value: 'MISSING_PERSON', label: 'Missing Person', icon: Search },
  { value: 'INSURANCE_FRAUD', label: 'Fraud Investigation', icon: Scale },
  { value: 'LEGAL_SUPPORT', label: 'Legal Support / Litigation', icon: Scale },
  // MASTER DIRECTIVE #23 — Vehicle Repossession
  { value: 'VEHICLE_REPOSSESSION', label: 'Vehicle Repossession', icon: Car },
  { value: 'WORKERS_COMP', label: 'Workers Compensation', icon: Briefcase },
  { value: 'SKIP_TRACE', label: 'Skip Trace / Locate Person', icon: Search },
  { value: 'STOLEN_PROPERTY', label: 'Stolen Property Recovery', icon: Search },
  { value: 'DOMESTIC_VIOLENCE', label: 'Domestic Violence Documentation', icon: UserX },
  { value: 'CRIMINAL_DEFENSE', label: 'Criminal Defense Support', icon: Scale },
  { value: 'OTHER', label: 'Other (describe below)', icon: Briefcase },
];

const URGENCY = [
  { value: 'ROUTINE', label: 'Routine (within 5–7 days)' },
  { value: 'PRIORITY', label: 'Priority (within 48 hours)' },
  { value: 'EMERGENCY', label: 'Emergency (same-day)' },
];

const HEARD_FROM = [
  'Google / Search', 'Attorney Referral', 'Prior Client',
  'Friend / Family', 'Social Media', 'Bail Bond / Court', 'Other',
];

// Small helper
const Field = ({ label, required, children, full, hint }) => (
  <div className={full ? 'md:col-span-2' : ''}>
    <Label className="text-[11px] font-mono uppercase tracking-wider text-slate-200">
      {label}{required && <span className="text-amber-300"> *</span>}
    </Label>
    <div className="mt-1">{children}</div>
    {hint && <p className="text-[10px] text-slate-400 mt-1">{hint}</p>}
  </div>
);

const inputCls =
  'bg-[#0F2340] border-white/20 !text-white placeholder:text-slate-500 focus-visible:ring-amber-400';

const ClientIntakeLanding = () => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);

  const [f, setF] = useState({
    // Client
    full_name: '', primary_email: '', primary_phone: '', alt_phone: '',
    address: '', city: '', state: '', postal_code: '',
    preferred_contact: 'PHONE', best_time_to_contact: '',
    heard_from: '',
    // Case
    case_type: '', urgency: 'ROUTINE',
    incident_date: '', incident_location: '',
    case_description: '',
    desired_outcome: '',
    budget_range: '',
    // MASTER DIRECTIVE #24 — expanded intake sections
    // Multiple subjects (array)
    subjects: [], // {full_name, relationship, aliases, dob, address, phone, employer, physical_description, vehicle_desc, details}
    // Client criminal history disclosure (helps the investigator assess safety)
    client_criminal_history: '',
    client_active_court_case: '',
    // Leads / notes (freeform)
    leads_notes: '',
    // Attorney info (if applicable)
    attorney_name: '', attorney_firm: '', attorney_phone: '', attorney_email: '', attorney_case_reference: '',
    // Police report info
    police_department: '', police_report_number: '',
    detective_name: '', detective_phone: '', detective_email: '',
    // Consents
    consent_contact: false,
    consent_retainer_understanding: false,
    consent_confidentiality: false,
    typed_signature: '',
  });
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  // MASTER DIRECTIVE #24 — multiple-subjects array helpers.
  const addSubject = () => set('subjects', [...f.subjects, {
    full_name: '', relationship: '', aliases: '', dob: '', address: '',
    phone: '', employer: '', physical_description: '', vehicle_desc: '', details: '',
  }]);
  const updateSubject = (idx, k, v) => set('subjects',
    f.subjects.map((s, i) => (i === idx ? { ...s, [k]: v } : s)));
  const removeSubject = (idx) => set('subjects',
    f.subjects.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    setError('');

    if (!f.full_name.trim() || !f.primary_email.trim() || !f.primary_phone.trim()) {
      setError('Your name, email and phone are required so we can reach you.');
      return;
    }
    if (!f.case_type) {
      setError('Please select the type of investigation you need.');
      return;
    }
    if (!f.case_description.trim() || f.case_description.trim().length < 30) {
      setError('Please describe your situation in at least a couple of sentences (30+ characters).');
      return;
    }
    if (!f.consent_contact || !f.consent_retainer_understanding || !f.consent_confidentiality) {
      setError('Please review and acknowledge all three consent items before submitting.');
      return;
    }
    if (!f.typed_signature.trim()) {
      setError('Please type your legal name as your electronic signature.');
      return;
    }

    setBusy(true);
    try {
      const payload = {
        client: {
          full_name: f.full_name.trim(),
          primary_email: f.primary_email.trim().toLowerCase(),
          email: f.primary_email.trim().toLowerCase(), // backend also checks this alias
          primary_phone: f.primary_phone.trim(),
          alt_phone: f.alt_phone.trim() || null,
          address: f.address.trim() || null,
          city: f.city.trim() || null,
          state: f.state.trim() || null,
          postal_code: f.postal_code.trim() || null,
          preferred_contact: f.preferred_contact,
          best_time_to_contact: f.best_time_to_contact.trim() || null,
          heard_from: f.heard_from || null,
          // MASTER DIRECTIVE #24 — client criminal history disclosure
          criminal_history: f.client_criminal_history.trim() || null,
          active_court_case: f.client_active_court_case.trim() || null,
        },
        case_details: {
          case_type: f.case_type,
          urgency: f.urgency,
          incident_date: f.incident_date || null,
          incident_location: f.incident_location.trim() || null,
          case_description: f.case_description.trim(),
          desired_outcome: f.desired_outcome.trim() || null,
          budget_range: f.budget_range.trim() || null,
          // MASTER DIRECTIVE #24 — leads / notes / attorney / police
          leads_notes: f.leads_notes.trim() || null,
          attorney: (f.attorney_name.trim() || f.attorney_firm.trim() || f.attorney_phone.trim() || f.attorney_email.trim()) ? {
            name: f.attorney_name.trim() || null,
            firm: f.attorney_firm.trim() || null,
            phone: f.attorney_phone.trim() || null,
            email: f.attorney_email.trim() || null,
            case_reference: f.attorney_case_reference.trim() || null,
          } : null,
          police_report: (f.police_department.trim() || f.police_report_number.trim() || f.detective_name.trim()) ? {
            department: f.police_department.trim() || null,
            report_number: f.police_report_number.trim() || null,
            detective_name: f.detective_name.trim() || null,
            detective_phone: f.detective_phone.trim() || null,
            detective_email: f.detective_email.trim() || null,
          } : null,
        },
        // MASTER DIRECTIVE #24 — multiple subjects. Only include ones with names.
        subjects: f.subjects
          .filter(s => (s.full_name || '').trim())
          .map(s => ({
            full_name: (s.full_name || '').trim(),
            relationship: (s.relationship || '').trim() || null,
            aliases: (s.aliases || '').trim() || null,
            dob: s.dob || null,
            address: (s.address || '').trim() || null,
            phone: (s.phone || '').trim() || null,
            employer: (s.employer || '').trim() || null,
            physical_description: (s.physical_description || '').trim() || null,
            vehicle_desc: (s.vehicle_desc || '').trim() || null,
            details: (s.details || '').trim() || null,
          })),
        vehicles: [],
        retainer_acknowledged: !!f.consent_retainer_understanding,
        legal_acknowledged: !!f.consent_confidentiality,
        signature: {
          typed_name: f.typed_signature.trim(),
          signed_at: new Date().toISOString(),
        },
      };

      const fd = new FormData();
      fd.append('intake_data', JSON.stringify(payload));
      // No files on public landing form — client can attach documents once
      // we open the case with them.

      const res = await fetch(`${BACKEND_URL}/api/client-intake/submit`, {
        method: 'POST',
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || 'Submission failed');
      setDone(data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#030812] flex items-center justify-center px-4 py-12" data-testid="intake-success-screen">
        <div className="max-w-lg w-full bg-[#0A1628] border border-emerald-500/40 rounded-lg p-8 text-center">
          <img src="/nexus-patch.png" alt="Nexus" className="w-24 h-24 mx-auto mb-3 object-contain" />
          <CheckCircle2 className="w-14 h-14 mx-auto mb-3 text-emerald-400" />
          <h1 className="text-2xl font-heading font-bold text-white mb-1">Request received</h1>
          <p className="text-sm text-slate-300 mb-3">
            Your intake reference is{' '}
            <span className="font-mono text-amber-300" data-testid="intake-reference">
              {done.intake_id}
            </span>
          </p>
          <p className="text-sm text-slate-300 mb-6">
            {done.message || 'A Nexus intake specialist will contact you within 24–48 hours to schedule a consultation.'}
          </p>
          <div className="flex flex-col gap-2">
            <Button
              onClick={() => navigate('/login')}
              className="bg-amber-400 hover:bg-amber-300 text-[#0B192C] font-heading font-semibold rounded-md"
              data-testid="intake-back-to-login"
            >
              Return to Sign In
            </Button>
            <Link to="/client-intake" className="text-[10px] text-slate-500 font-mono uppercase tracking-widest hover:text-slate-300">
              Submit another request
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[#030812] text-white overflow-x-hidden" data-testid="client-intake-landing">
      {/* Iter135 — back-to-login header */}
      <div className="absolute top-4 left-4 z-20">
        <Button type="button" variant="outline"
          onClick={() => navigate('/login')}
          className="h-9 border-amber-500/40 !text-amber-200 hover:!bg-amber-500/10 font-mono uppercase tracking-wider text-[11px]"
          data-testid="intake-landing-back-to-login">
          ← Back to Login
        </Button>
      </div>
      {/* ============ HERO ============ */}
      <div className="relative">
        <div
          aria-hidden
          className="absolute inset-0 bg-no-repeat bg-cover"
          style={{
            backgroundImage: 'url(/nexus-hero-dallas-eagle.jpg)',
            backgroundPosition: 'center center',
            filter: 'brightness(0.35) saturate(1.05)',
          }}
        />
        <div className="relative max-w-4xl mx-auto px-4 pt-12 pb-14 text-center">
          <img src="/nexus-patch.png" alt="NexusTrac" className="w-24 h-24 sm:w-28 sm:h-28 mx-auto object-contain mb-3" />
          <p className="text-[10px] font-mono text-amber-300 uppercase tracking-[0.32em]">Nexus Investigation Agency</p>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-heading font-bold mt-2 tracking-tight">
            Confidential Case Intake
          </h1>
          <p className="text-sm sm:text-base text-slate-200 mt-3 max-w-2xl mx-auto leading-relaxed">
            Licensed Texas investigators — surveillance, infidelity, custody, background,
            asset search, missing persons, and legal support. Tell us what you need. A
            senior investigator will review your case and reach out within{' '}
            <span className="text-amber-300 font-semibold">24–48 hours</span>.
          </p>
          <div className="flex items-center justify-center gap-4 mt-5 text-[11px] font-mono uppercase tracking-widest text-slate-300">
            <span className="inline-flex items-center gap-1.5"><Lock size={12} /> Encrypted</span>
            <span className="inline-flex items-center gap-1.5"><ShieldCheck size={12} /> Confidential</span>
            <span className="inline-flex items-center gap-1.5"><Award size={12} /> TX Licensed</span>
          </div>
          <a href="#intake-form" className="inline-block mt-6">
            <Button className="bg-amber-400 hover:bg-amber-300 text-[#0B192C] font-heading font-semibold rounded-md px-6" data-testid="intake-start-btn">
              Start My Case Request <ChevronRight size={16} className="ml-1" />
            </Button>
          </a>
          <div className="mt-3">
            <Link to="/login" className="text-[10px] text-slate-400 font-mono uppercase tracking-widest hover:text-white">
              Existing client? Sign in →
            </Link>
          </div>
        </div>
      </div>

      {/* ============ 3-STEP OVERVIEW ============ */}
      <div className="max-w-4xl mx-auto px-4 py-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { icon: Eye,   step: '01', title: 'Tell Us What Happened', body: 'Answer a few private questions about the situation and who is involved.' },
          { icon: Clock, step: '02', title: 'We Review Within 48h',  body: 'A senior investigator reviews your intake and schedules a confidential consult.' },
          { icon: ShieldCheck, step: '03', title: 'Case Opened',     body: 'On approval we sign a retainer and begin the operational plan under NDA.' },
        ].map(s => {
          const Icon = s.icon;
          return (
            <div key={s.step} className="rounded-lg border border-white/10 bg-[#0A1628]/70 p-4">
              <div className="flex items-center gap-2 text-amber-300">
                <Icon size={18} />
                <span className="text-[10px] font-mono uppercase tracking-widest">Step {s.step}</span>
              </div>
              <h3 className="text-white font-heading font-semibold mt-2">{s.title}</h3>
              <p className="text-slate-300 text-sm mt-1">{s.body}</p>
            </div>
          );
        })}
      </div>

      {/* ============ INTAKE FORM ============ */}
      <div id="intake-form" className="max-w-3xl mx-auto px-4 pb-16">
        <div className="bg-[#0A1628]/85 border border-white/10 rounded-lg p-6 sm:p-8 backdrop-blur">
          <div className="mb-6">
            <p className="text-[10px] font-mono uppercase tracking-[0.32em] text-amber-300">Intake Form</p>
            <h2 className="text-xl sm:text-2xl font-heading font-bold mt-1">Tell us about your case</h2>
            <p className="text-xs text-slate-400 mt-1">
              All information is confidential and reviewed only by licensed Nexus investigators.
            </p>
          </div>

          {error && (
            <div className="flex items-start gap-2 p-3 mb-5 bg-red-500/10 border border-red-500/40 rounded text-red-300 text-sm" data-testid="intake-error">
              <AlertCircle size={14} className="mt-0.5 flex-shrink-0" /> {error}
            </div>
          )}

          <form onSubmit={submit} className="space-y-8">
            {/* ------- 1. Your contact info ------- */}
            <section>
              <h3 className="text-sm font-heading font-semibold text-amber-300 uppercase tracking-widest border-b border-white/10 pb-2 mb-4">
                1. Your Contact Info
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Full Name" required>
                  <Input value={f.full_name} onChange={e => set('full_name', e.target.value)} className={inputCls} data-testid="intake-full-name" />
                </Field>
                <Field label="Email" required>
                  <Input type="email" value={f.primary_email} onChange={e => set('primary_email', e.target.value)} className={inputCls} data-testid="intake-email" />
                </Field>
                <Field label="Primary Phone" required>
                  <Input value={f.primary_phone} onChange={e => set('primary_phone', e.target.value)} className={inputCls} data-testid="intake-phone" placeholder="(555) 123-4567" />
                </Field>
                <Field label="Alternate Phone">
                  <Input value={f.alt_phone} onChange={e => set('alt_phone', e.target.value)} className={inputCls} />
                </Field>
                <Field label="Address">
                  <Input value={f.address} onChange={e => set('address', e.target.value)} className={inputCls} />
                </Field>
                <Field label="City">
                  <Input value={f.city} onChange={e => set('city', e.target.value)} className={inputCls} />
                </Field>
                <Field label="State">
                  <Input value={f.state} onChange={e => set('state', e.target.value)} className={inputCls} placeholder="TX" />
                </Field>
                <Field label="ZIP">
                  <Input value={f.postal_code} onChange={e => set('postal_code', e.target.value)} className={inputCls} />
                </Field>
                <Field label="Preferred Contact Method">
                  <select
                    value={f.preferred_contact}
                    onChange={e => set('preferred_contact', e.target.value)}
                    className="w-full bg-[#0F2340] border border-white/20 rounded px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                    data-testid="intake-contact-method"
                  >
                    <option value="PHONE">Phone Call</option>
                    <option value="TEXT">Text Message</option>
                    <option value="EMAIL">Email</option>
                  </select>
                </Field>
                <Field label="Best Time to Contact">
                  <Input value={f.best_time_to_contact} onChange={e => set('best_time_to_contact', e.target.value)} className={inputCls} placeholder="Weekdays after 5pm" />
                </Field>
                <Field full label="How did you hear about us?">
                  <select
                    value={f.heard_from}
                    onChange={e => set('heard_from', e.target.value)}
                    className="w-full bg-[#0F2340] border border-white/20 rounded px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                    data-testid="intake-heard-from"
                  >
                    <option value="">— Select —</option>
                    {HEARD_FROM.map(h => <option key={h} value={h}>{h}</option>)}
                  </select>
                </Field>
              </div>
            </section>

            {/* ------- 2. Case type ------- */}
            <section>
              <h3 className="text-sm font-heading font-semibold text-amber-300 uppercase tracking-widest border-b border-white/10 pb-2 mb-4">
                2. Type of Investigation
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="intake-case-type-grid">
                {CASE_TYPES.map(t => {
                  const Icon = t.icon;
                  const selected = f.case_type === t.value;
                  return (
                    <button
                      type="button"
                      key={t.value}
                      onClick={() => set('case_type', t.value)}
                      className={`text-left rounded-md border px-3 py-2.5 flex items-center gap-2.5 transition-all ${
                        selected
                          ? 'border-amber-400 bg-amber-400/10 text-white'
                          : 'border-white/15 bg-[#0F2340] text-slate-200 hover:border-white/30'
                      }`}
                      data-testid={`intake-case-${t.value}`}
                    >
                      <Icon size={16} className={selected ? 'text-amber-300' : 'text-slate-400'} />
                      <span className="text-sm">{t.label}</span>
                    </button>
                  );
                })}
              </div>
              <div className="mt-4">
                <Field label="How soon do you need this?">
                  <div className="flex flex-col sm:flex-row gap-2 mt-1">
                    {URGENCY.map(u => (
                      <button
                        type="button"
                        key={u.value}
                        onClick={() => set('urgency', u.value)}
                        className={`flex-1 text-xs font-mono uppercase tracking-wider rounded px-3 py-2 border ${
                          f.urgency === u.value
                            ? 'border-amber-400 bg-amber-400/10 text-white'
                            : 'border-white/15 bg-[#0F2340] text-slate-300 hover:border-white/30'
                        }`}
                        data-testid={`intake-urgency-${u.value}`}
                      >
                        {u.label}
                      </button>
                    ))}
                  </div>
                </Field>
              </div>
            </section>

            {/* ------- 3. Case details ------- */}
            <section>
              <h3 className="text-sm font-heading font-semibold text-amber-300 uppercase tracking-widest border-b border-white/10 pb-2 mb-4">
                3. Case Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Date of Incident (if applicable)">
                  <Input type="date" value={f.incident_date} onChange={e => set('incident_date', e.target.value)} className={inputCls} />
                </Field>
                <Field label="Location of Incident">
                  <Input value={f.incident_location} onChange={e => set('incident_location', e.target.value)} className={inputCls} placeholder="City, address, or general area" />
                </Field>
                <Field full label="Describe the situation" required
                  hint="Be as specific as safely possible. Everything here is confidential.">
                  <Textarea
                    rows={5}
                    value={f.case_description}
                    onChange={e => set('case_description', e.target.value)}
                    className={inputCls}
                    placeholder="What has been happening? Who is involved? What have you already tried?"
                    data-testid="intake-description"
                  />
                </Field>
                <Field full label="What outcome are you hoping for?"
                  hint="Documented evidence, service of process, courtroom testimony, peace of mind, etc.">
                  <Textarea rows={3} value={f.desired_outcome} onChange={e => set('desired_outcome', e.target.value)} className={inputCls} data-testid="intake-outcome" />
                </Field>
                <Field label="Budget Range (optional)">
                  <Input value={f.budget_range} onChange={e => set('budget_range', e.target.value)} className={inputCls} placeholder="e.g. $1,500–$3,500" />
                </Field>
              </div>
            </section>

            {/* ------- 4. Subjects (MASTER DIRECTIVE #24 — multiple subjects) ------- */}
            <section>
              <h3 className="text-sm font-heading font-semibold text-amber-300 uppercase tracking-widest border-b border-white/10 pb-2 mb-4">
                4. Persons of Interest (Optional)
              </h3>
              <p className="text-xs text-slate-400 mb-3">
                Add every subject the investigation may involve. All details help us
                plan safely and lawfully.
              </p>
              <div className="space-y-3" data-testid="intake-subjects-list">
                {f.subjects.map((s, i) => (
                  <div key={i} className="border border-white/10 rounded-md p-3 bg-[#0F2340]">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-[10px] font-mono uppercase tracking-widest text-amber-300">Subject #{i + 1}</p>
                      <button type="button" onClick={() => removeSubject(i)}
                        className="text-[10px] text-red-300 hover:text-red-200 font-mono uppercase inline-flex items-center gap-1"
                        data-testid={`intake-subject-remove-${i}`}
                      >
                        <X size={11} /> Remove
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Field label="Full Name" required={i === 0 && !f.subjects[0]?.full_name}>
                        <Input value={s.full_name} onChange={e => updateSubject(i, 'full_name', e.target.value)}
                          className={inputCls} data-testid={`intake-subject-name-${i}`} />
                      </Field>
                      <Field label="Relationship to You">
                        <Input value={s.relationship} onChange={e => updateSubject(i, 'relationship', e.target.value)}
                          className={inputCls} placeholder="Spouse / Ex / Business partner" />
                      </Field>
                      <Field label="Aliases / Nicknames">
                        <Input value={s.aliases} onChange={e => updateSubject(i, 'aliases', e.target.value)} className={inputCls} />
                      </Field>
                      <Field label="Date of Birth (approx.)">
                        <Input type="date" value={s.dob} onChange={e => updateSubject(i, 'dob', e.target.value)} className={inputCls} />
                      </Field>
                      <Field label="Phone">
                        <Input value={s.phone} onChange={e => updateSubject(i, 'phone', e.target.value)} className={inputCls} />
                      </Field>
                      <Field label="Employer / Workplace">
                        <Input value={s.employer} onChange={e => updateSubject(i, 'employer', e.target.value)} className={inputCls} />
                      </Field>
                      <Field full label="Last Known Address">
                        <Input value={s.address} onChange={e => updateSubject(i, 'address', e.target.value)} className={inputCls} />
                      </Field>
                      <Field full label="Physical Description">
                        <Input value={s.physical_description} onChange={e => updateSubject(i, 'physical_description', e.target.value)}
                          className={inputCls} placeholder="Height, build, hair, distinctive marks" />
                      </Field>
                      <Field full label="Vehicle (make, model, color, plate)">
                        <Input value={s.vehicle_desc} onChange={e => updateSubject(i, 'vehicle_desc', e.target.value)} className={inputCls} />
                      </Field>
                      <Field full label="Anything else">
                        <Textarea rows={2} value={s.details} onChange={e => updateSubject(i, 'details', e.target.value)}
                          className={inputCls} placeholder="Patterns, safety concerns, prior police contact…" />
                      </Field>
                    </div>
                  </div>
                ))}
                <button type="button" onClick={addSubject}
                  className="text-xs font-mono uppercase tracking-wider bg-amber-400/90 text-[#0B192C] px-3 py-1.5 rounded-sm hover:bg-amber-300 inline-flex items-center gap-1.5"
                  data-testid="intake-subject-add"
                >
                  <Plus size={12} /> Add Subject
                </button>
              </div>
            </section>

            {/* ------- 4b. Client Criminal History (MASTER DIRECTIVE #24) ------- */}
            <section>
              <h3 className="text-sm font-heading font-semibold text-amber-300 uppercase tracking-widest border-b border-white/10 pb-2 mb-4">
                4b. Your Prior Court / Criminal History (Optional)
              </h3>
              <p className="text-xs text-slate-400 mb-3">
                Confidential. Helps our investigators understand context and any court
                restrictions we need to respect.
              </p>
              <div className="grid grid-cols-1 gap-4">
                <Field full label="Any prior criminal history you want us to know about?">
                  <Textarea rows={2} value={f.client_criminal_history} onChange={e => set('client_criminal_history', e.target.value)}
                    className={inputCls} data-testid="intake-client-crim-history"
                    placeholder="Optional — arrests, convictions, protective orders." />
                </Field>
                <Field full label="Are you currently a party to any active court case (civil or criminal)?">
                  <Textarea rows={2} value={f.client_active_court_case} onChange={e => set('client_active_court_case', e.target.value)}
                    className={inputCls} placeholder="Court, case number, hearing dates if known." />
                </Field>
              </div>
            </section>

            {/* ------- 4c. Leads / Notes (MASTER DIRECTIVE #24) ------- */}
            <section>
              <h3 className="text-sm font-heading font-semibold text-amber-300 uppercase tracking-widest border-b border-white/10 pb-2 mb-4">
                4c. Leads & Investigative Notes
              </h3>
              <Field full label="Any leads, addresses, timelines, screenshots, or other details we should know">
                <Textarea rows={4} value={f.leads_notes} onChange={e => set('leads_notes', e.target.value)}
                  className={inputCls} data-testid="intake-leads-notes"
                  placeholder="Everything you've already documented on your own — even hunches." />
              </Field>
            </section>

            {/* ------- 4d. Attorney Info (MASTER DIRECTIVE #24) ------- */}
            <section>
              <h3 className="text-sm font-heading font-semibold text-amber-300 uppercase tracking-widest border-b border-white/10 pb-2 mb-4 flex items-center gap-2">
                <Gavel size={14} /> 4d. Attorney Information (Optional)
              </h3>
              <p className="text-xs text-slate-400 mb-3">
                If you're already represented, we'll coordinate under attorney-client
                privilege where applicable.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Attorney Name"><Input value={f.attorney_name} onChange={e => set('attorney_name', e.target.value)} className={inputCls} data-testid="intake-attorney-name" /></Field>
                <Field label="Law Firm"><Input value={f.attorney_firm} onChange={e => set('attorney_firm', e.target.value)} className={inputCls} /></Field>
                <Field label="Attorney Phone"><Input value={f.attorney_phone} onChange={e => set('attorney_phone', e.target.value)} className={inputCls} /></Field>
                <Field label="Attorney Email"><Input type="email" value={f.attorney_email} onChange={e => set('attorney_email', e.target.value)} className={inputCls} /></Field>
                <Field full label="Attorney Case Reference / Docket #">
                  <Input value={f.attorney_case_reference} onChange={e => set('attorney_case_reference', e.target.value)} className={inputCls} />
                </Field>
              </div>
            </section>

            {/* ------- 4e. Police Report Info (MASTER DIRECTIVE #24) ------- */}
            <section>
              <h3 className="text-sm font-heading font-semibold text-amber-300 uppercase tracking-widest border-b border-white/10 pb-2 mb-4 flex items-center gap-2">
                <FileWarning size={14} /> 4e. Police / Sheriff Report (Optional)
              </h3>
              <p className="text-xs text-slate-400 mb-3">
                If a report has already been filed, provide as much as you know.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Police / Sheriff Department"><Input value={f.police_department} onChange={e => set('police_department', e.target.value)} className={inputCls} data-testid="intake-police-dept" placeholder="e.g. Dallas PD" /></Field>
                <Field label="Report / Incident Number"><Input value={f.police_report_number} onChange={e => set('police_report_number', e.target.value)} className={inputCls} /></Field>
                <Field label="Detective / Officer Name"><Input value={f.detective_name} onChange={e => set('detective_name', e.target.value)} className={inputCls} /></Field>
                <Field label="Detective Phone"><Input value={f.detective_phone} onChange={e => set('detective_phone', e.target.value)} className={inputCls} /></Field>
                <Field full label="Detective Email"><Input type="email" value={f.detective_email} onChange={e => set('detective_email', e.target.value)} className={inputCls} /></Field>
              </div>
            </section>

            {/* ------- 5. Acknowledgements ------- */}
            <section>
              <h3 className="text-sm font-heading font-semibold text-amber-300 uppercase tracking-widest border-b border-white/10 pb-2 mb-4">
                5. Acknowledgements & Signature
              </h3>
              <div className="space-y-3">
                <label className="flex items-start gap-2.5 text-sm text-slate-100">
                  <Checkbox
                    checked={f.consent_contact}
                    onCheckedChange={v => set('consent_contact', !!v)}
                    data-testid="intake-consent-contact"
                    className="mt-0.5"
                  />
                  <span>
                    I authorize Nexus Investigation Agency to contact me using the phone / email
                    I provided. I understand this is <b>not</b> yet an active case — I am
                    requesting a confidential consultation.
                  </span>
                </label>
                <label className="flex items-start gap-2.5 text-sm text-slate-100">
                  <Checkbox
                    checked={f.consent_retainer_understanding}
                    onCheckedChange={v => set('consent_retainer_understanding', !!v)}
                    data-testid="intake-consent-retainer"
                    className="mt-0.5"
                  />
                  <span>
                    I understand that investigative services require a signed retainer
                    agreement before any field work begins, and that submitting this intake
                    does not obligate me to hire Nexus.
                  </span>
                </label>
                <label className="flex items-start gap-2.5 text-sm text-slate-100">
                  <Checkbox
                    checked={f.consent_confidentiality}
                    onCheckedChange={v => set('consent_confidentiality', !!v)}
                    data-testid="intake-consent-conf"
                    className="mt-0.5"
                  />
                  <span>
                    I certify that the information I have provided is truthful. I understand
                    Nexus is licensed by TX DPS and operates strictly within the law.
                  </span>
                </label>
                <div className="pt-2">
                  <Field label="Electronic Signature (type your full legal name)" required>
                    <Input
                      value={f.typed_signature}
                      onChange={e => set('typed_signature', e.target.value)}
                      className={inputCls}
                      placeholder="e.g. Jane P. Doe"
                      data-testid="intake-signature"
                    />
                  </Field>
                </div>
              </div>
            </section>

            {/* Submit */}
            <div className="flex items-center justify-between gap-3 pt-2">
              <Link to="/login" className="text-xs text-slate-400 hover:text-white font-mono uppercase tracking-wider" data-testid="intake-cancel">
                ← Back to Sign In
              </Link>
              <Button
                type="submit"
                disabled={busy}
                className="bg-amber-400 hover:bg-amber-300 text-[#0B192C] font-heading font-semibold rounded-md px-8"
                data-testid="intake-submit-btn"
              >
                {busy ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</>) : 'Submit My Request'}
              </Button>
            </div>
          </form>
        </div>

        <p className="text-[10px] text-slate-500 font-mono uppercase tracking-widest text-center mt-6">
          Nexus Investigation Agency · TX DPS Licensed · Encrypted Submission
        </p>
      </div>
    </div>
  );
};

export default ClientIntakeLanding;
